import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Heart, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

import { getMenu, type MenuResponse } from '@/services/client';
import { useCart } from '@/store/cart.store';
import { useFavorites } from '@/store/favorites.store';
import DishCard from '@/components/DishCard';

type Dish = MenuResponse['categories'][number]['dishes'][number];

export default function Favorites() {
  const navigate = useNavigate();
  const estId = useCart((s) => s.establishmentId);
  const add = useCart((s) => s.add);
  const favIds = useFavorites((s) => s.ids);
  const clearFav = useFavorites((s) => s.clear);

  const { data, isLoading } = useQuery({
    queryKey: ['client/menu', estId],
    queryFn: () => getMenu(estId!),
    enabled: !!estId,
  });

  const dishes = useMemo<Dish[]>(() => {
    if (!data) return [];
    const flat = data.categories.flatMap((c) => c.dishes);
    return favIds
      .map((id) => flat.find((d) => d.id === id))
      .filter((d): d is Dish => !!d);
  }, [data, favIds]);

  return (
    <div className="pt-4">
      <div className="px-4 mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Избранное</h1>
          <p className="text-sm text-slate-500">Ваши любимые позиции</p>
        </div>
        {favIds.length > 0 && (
          <button
            className="text-xs text-slate-400 flex items-center gap-1 hover:text-rose-600"
            onClick={() => {
              if (confirm('Очистить избранное?')) clearFav();
            }}
          >
            <Trash2 size={14} /> Очистить
          </button>
        )}
      </div>

      {!estId || isLoading ? (
        <div className="p-6 text-center text-slate-400">Загрузка…</div>
      ) : favIds.length === 0 ? (
        <EmptyState />
      ) : dishes.length === 0 ? (
        <div className="px-4 text-center text-slate-400 py-8">
          Избранное из другого заведения. Добавьте позиции заново.
        </div>
      ) : (
        <div className="px-4 space-y-2.5">
          {dishes.map((d) => (
            <DishCard
              key={d.id}
              dish={d}
              variant="row"
              onOpen={() => navigate(`/menu/${d.id}`)}
              onQuickAdd={() => {
                add({ dishId: d.id, name: d.name, price: d.price, photoUrl: d.photoUrl, modifiers: [] });
                toast.success(`${d.name} добавлен`);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="px-6 py-12 text-center">
      <div className="w-16 h-16 rounded-full bg-rose-50 text-rose-500 mx-auto flex items-center justify-center mb-3">
        <Heart size={28} />
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Здесь появятся блюда, которые вы отметите сердечком в меню.
      </p>
      <Link to="/menu" className="btn-primary">Перейти в меню</Link>
    </div>
  );
}

